// Ask Ogma's side window (Craig, 30 Oct 2026; docs/specs/CAMPAIGN_LED_2026_10.md "11"): an answer that is a thing opens
// beside the chat and can be exported. A table as a spreadsheet (CSV), a chart with its figures (CSV), a draft campaign
// or a firm's story as a document (a Word file, which Word, Pages and Google Docs open). And a drafted campaign becomes
// what saving needs, from the same defaults a new campaign starts from.
import type { CampaignInput } from "./campaigns";
import type { CampaignDraft, ChatChart, ChatSide } from "./chat";
import { newCampaign, readPlan, toInput } from "./campaignEditor";
import { KNOWN_RULE_DEFAULTS, REACH_LABEL } from "./campaignReach";
import { toCsv } from "./csv";
import { humanise } from "./format";
import type { JsonObject } from "./types";

const day = () => new Date().toISOString().slice(0, 10);
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "ogma";

/** A chart's figures as a spreadsheet: one row per point. */
export function chartCsv(c: ChatChart): string {
  const ratio = c.result.rows.some((r) => r.numerator !== undefined && r.numerator !== null);
  return toCsv(c.result.rows, [
    { header: humanise(String(c.result.by || "label")), value: (r) => r.label },
    { header: c.result.label || c.title, value: (r) => r.value },
    ...(ratio ? [{ header: "Of", value: (r: (typeof c.result.rows)[number]) => r.denominator }, { header: "Count", value: (r: (typeof c.result.rows)[number]) => r.numerator }] : []),
  ]);
}

/** The file a side window exports, or null for a page (it has its own downloads). */
export function sideFile(s: ChatSide, firmName?: string | null): { filename: string; type: "csv" | "doc"; content: string } | null {
  if (s.kind === "page") return null;
  if (s.kind === "quote") return { filename: `${slug(String(s.quote.label ?? "quote"))}-${day()}.doc`, type: "doc", content: quoteDoc(s.quote) };
  if (s.kind === "table") {
    const f = s.file;
    return { filename: f.filename.endsWith(".csv") ? f.filename : `${f.filename}.csv`, type: "csv",
      content: toCsv(f.rows, f.columns.map((c) => ({ header: c, value: (r: Record<string, unknown>) => r[c] }))) };
  }
  if (s.kind === "chart") return { filename: `${slug(s.chart.title)}-${day()}.csv`, type: "csv", content: chartCsv(s.chart) };
  if (s.kind === "campaign") return { filename: `${slug(s.draft.name)}-draft.doc`, type: "doc", content: campaignDoc(s.draft) };
  return { filename: `${slug(firmName ?? "firm")}-${day()}.doc`, type: "doc", content: firmDoc(firmName ?? "This firm", s.items) };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function doc(title: string, body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title></head><body style="font-family:Calibri,Arial,sans-serif">` +
    `<h1>${esc(title)}</h1>${body}</body></html>`;
}

/** The lines a draft campaign shows, in the order a person reads them (the side window and its document). */
export function draftLines(d: CampaignDraft): { label: string; value: string }[] {
  const r = d.known_rule;
  const which = !r ? null : r.firms.kind === "all" ? "All firms you know" : `${humanise(r.firms.kind)}: ${r.firms.value}`;
  return [
    { label: "Who it is for", value: REACH_LABEL[d.reach] },
    ...(d.reach !== "known" ? [
      { label: "Who to target", value: d.who ?? "Not said yet" },
      ...(d.where ? [{ label: "Where", value: d.where }] : []),
      ...(d.size ? [{ label: "Size", value: d.size }] : []),
      ...(d.notes ? [{ label: "What makes a good fit", value: d.notes }] : []),
      { label: "The offer", value: d.offer ?? "Not said yet" },
      ...(d.first_ask ? [{ label: "First ask", value: d.first_ask }] : []),
    ] : []),
    ...(r ? [
      { label: "Last spoken to", value: `More than ${r.quiet_days} days ago` },
      { label: "Which firms", value: which! },
      { label: "Topic", value: r.topic ?? "Pick up where you left off" },
      { label: "Daily limit", value: `${r.daily_limit} a day` },
    ] : []),
  ];
}

/** One thing a campaign needs, as the side window's checklist shows it (Craig, 31 Oct 2026: walk me through what is
 * needed, and let me change it there). */
export type StepKey = "reach" | "who" | "where" | "offer" | "first_ask" | "quiet_days" | "topic" | "daily_limit";
export interface DraftStep {
  key: StepKey;
  label: string;
  /** What it says now, or null when it is still to do. */
  value: string | null;
  /** What the person types to change it (the raw value, not the words shown). */
  raw: string;
  input: "text" | "long" | "number" | "reach";
  /** Ogma asks for this one before Review and confirm. */
  needed: boolean;
}

/** The checklist for a draft, in the order Ogma asks: who it is for, then who and where, the offer and the first ask
 * for new firms; how long since you spoke and the topic for firms you know. */
export function draftSteps(d: CampaignDraft): DraftStep[] {
  const t = (v: string | null) => v ?? "";
  const steps: DraftStep[] = [{ key: "reach", label: "Who it is for", value: REACH_LABEL[d.reach], raw: d.reach, input: "reach", needed: true }];
  if (d.reach !== "known") {
    steps.push(
      { key: "who", label: "Who to target", value: d.who, raw: t(d.who), input: "long", needed: true },
      { key: "where", label: "Where", value: d.where, raw: t(d.where), input: "text", needed: true },
      { key: "offer", label: "The offer", value: d.offer, raw: t(d.offer), input: "long", needed: true },
      { key: "first_ask", label: "First ask", value: d.first_ask, raw: t(d.first_ask), input: "text", needed: true },
    );
  }
  const r = d.known_rule;
  if (r) {
    steps.push(
      { key: "quiet_days", label: "Last spoken to", value: `More than ${r.quiet_days} days ago`, raw: String(r.quiet_days), input: "number", needed: true },
      { key: "topic", label: "Topic", value: r.topic ?? null, raw: r.topic ?? "", input: "long", needed: false },
      { key: "daily_limit", label: "Daily limit", value: `${r.daily_limit} a day`, raw: String(r.daily_limit), input: "number", needed: false },
    );
  }
  return steps;
}

/** The draft with one thing changed by the person, kept within the bounds the supervisor keeps (lib/tools.ts). */
export function withStep(d: CampaignDraft, key: StepKey, raw: string): CampaignDraft {
  const v = raw.trim() || null;
  const whole = (lo: number, hi: number, dflt: number) => {
    const n = Math.round(Number(raw));
    return Number.isFinite(n) && raw.trim() ? Math.min(hi, Math.max(lo, n)) : dflt;
  };
  const rule = d.known_rule ?? KNOWN_RULE_DEFAULTS;
  switch (key) {
    case "reach": {
      const reach = raw === "known" || raw === "both" ? raw : "new";
      return { ...d, reach, known_rule: reach === "new" ? null : rule };
    }
    case "who": return { ...d, who: v?.slice(0, 300) ?? null };
    case "where": return { ...d, where: v?.slice(0, 300) ?? null };
    case "offer": return { ...d, offer: v?.slice(0, 600) ?? null };
    case "first_ask": return { ...d, first_ask: v?.slice(0, 600) ?? null };
    case "quiet_days": return { ...d, known_rule: { ...rule, quiet_days: whole(7, 730, rule.quiet_days) } };
    case "daily_limit": return { ...d, known_rule: { ...rule, daily_limit: whole(1, 100, rule.daily_limit) } };
    case "topic": {
      const { topic: _old, ...rest } = rule;
      return { ...d, known_rule: v ? { ...rest, topic: v.slice(0, 300) } : rest };
    }
  }
}

/** A quote's facts, in the order a person reads them (the side window and its document). */
export function quoteLines(q: Record<string, unknown>): { label: string; value: string }[] {
  const text = (v: unknown) => (v === null || v === undefined || v === "" ? null : typeof v === "object" ? null : String(v));
  const money = typeof q.total === "number" ? new Intl.NumberFormat("en-GB", { style: "currency", currency: typeof q.currency === "string" ? q.currency : "GBP" }).format(q.total) : null;
  const lines: [string, string | null][] = [
    ["Firm", text(q.organisation)], ["For", text(q.person)], ["Status", text(q.status) && humanise(String(q.status))],
    ["Total", money], ["Revision", text(q.revision)], ["Price list", text(q.book)], ["Priced", text(q.priced_at)?.slice(0, 10) ?? null],
    ["Outcome", text(q.outcome) && humanise(String(q.outcome))],
    ["Checks before it goes", Array.isArray(q.check_reasons) && q.check_reasons.length ? q.check_reasons.filter((x) => typeof x === "string").join("; ") : null],
  ];
  return lines.filter((l): l is [string, string] => l[1] !== null).map(([label, value]) => ({ label, value }));
}

export function quoteDoc(q: Record<string, unknown>): string {
  const rows = quoteLines(q).map((l) => `<tr><th align="left">${esc(l.label)}</th><td>${esc(l.value)}</td></tr>`).join("");
  return doc(String(q.label ?? "Quote"), `<table cellpadding="6">${rows}</table>`);
}

export function campaignDoc(d: CampaignDraft): string {
  const rows = draftLines(d).map((l) => `<tr><th align="left">${esc(l.label)}</th><td>${esc(l.value)}</td></tr>`).join("");
  return doc(`${d.name} (draft campaign)`, `<table cellpadding="6">${rows}</table><p>A draft: nothing is sent until the campaign is approved in Ogma.</p>`);
}

const cell = (v: unknown) => (v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));

export function firmDoc(name: string, items: Record<string, unknown>[]): string {
  const keys = ["at", "occurred_at", "kind", "channel", "step_id", "summary", "subject", "title"].filter((k) => items.some((i) => i[k] !== undefined && i[k] !== null));
  const head = keys.map((k) => `<th align="left">${esc(humanise(k))}</th>`).join("");
  const rows = items.map((i) => `<tr>${keys.map((k) => `<td>${esc(cell(i[k]))}</td>`).join("")}</tr>`).join("");
  return doc(name, items.length ? `<table cellpadding="4"><tr>${head}</tr>${rows}</table>` : "<p>Nothing recorded yet.</p>");
}

/** Hands a document to the browser to save. */
export function downloadDoc(filename: string, html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: "application/msword" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** What saving a drafted campaign sends: a new campaign's defaults (set-up's offer, the recommended follow-ups) with what
 * was said in the chat on top; for firms you know, the rule only. */
export function draftInput(d: CampaignDraft, config: JsonObject | null): CampaignInput {
  const base = newCampaign(config, d.name);
  const audience: JsonObject = { ...base.audience, ...(d.who ? { who: d.who } : {}), ...(d.where ? { where: d.where } : {}),
    ...(d.size ? { size: d.size } : {}), ...(d.notes ? { notes: d.notes } : {}) };
  const card: JsonObject = { ...base.card, ...(d.offer ? { what_we_offer: d.offer } : {}), ...(d.first_ask ? { smallest_yes: d.first_ask } : {}) };
  const c = { ...base, name: d.name, reach: d.reach, known_rule: d.known_rule, audience, card };
  const plan = readPlan(base.card);
  return toInput(c, plan);
}
